import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { TabName } from '../../types/common.types';
import { DEFAULT_MEDIA_CACHE_TTL, fetchMediaFile, fetchMediaFiles } from '../thunks/mediaThunks';
import type { CachedData, MediaState, RootState } from '../types';

const initialState: MediaState = {
  selectedTab: TabName.TRIAL_MANAGER,
  selectedTabFiles: { mediaFiles: {}, textFiles: [] },
  loading: false,
  error: null,
  // Cache for media files by tab
  mediaCache: {},
  // Cache for individual media files
  mediaFileCache: {},
};

export const mediaSlice = createSlice({
  name: 'media',
  initialState,
  reducers: {
    setSelectedTab: (state, action: PayloadAction<TabName>) => {
      state.selectedTab = action.payload;
    },
    // Add a reducer to clear caches
    clearCaches: (state) => {
      state.mediaCache = {};
      state.mediaFileCache = {};
    },
    // Add a reducer to cache a media file
    cacheMediaFile: (state, action: PayloadAction<CachedData<string> & { fileId: string }>) => {
      const { fileId, data: data, timestamp, expiresAt } = action.payload;
      state.mediaFileCache[fileId] = { data: data, timestamp, expiresAt };
    },
    // Add a reducer to remove a media file from cache
    removeMediaFileFromCache: (state, action: PayloadAction<string>) => {
      const fileId = action.payload;
      delete state.mediaFileCache[fileId];
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle fetchMediaFiles
      .addCase(fetchMediaFiles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMediaFiles.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedTabFiles = action.payload;

        // Use the tab name from the action meta (third argument passed to fulfilled)
        const tabName = action.meta.arg;
        state.mediaCache[tabName] = {
          data: action.payload,
          timestamp: Date.now(),
          expiresAt: Date.now() + DEFAULT_MEDIA_CACHE_TTL, // 5 minutes cache
        };
      })
      .addCase(fetchMediaFiles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        console.error('fetchMediaFiles rejected:', action.payload); // Debug error
      })
      // Handle fetchMediaFile
      .addCase(fetchMediaFile.fulfilled, (state, action) => {
        const fileId = action.meta.arg.fileId;
        state.mediaFileCache[fileId] = {
          data: action.payload,
          timestamp: Date.now(),
          expiresAt: Date.now() + DEFAULT_MEDIA_CACHE_TTL,
        };
      });
  },
});

export const { setSelectedTab, clearCaches, cacheMediaFile, removeMediaFileFromCache } =
  mediaSlice.actions;

export const selectSelectedTab = (state: RootState) => state.media.selectedTab;
export const selectSelectedTabFiles = (state: RootState) => state.media.selectedTabFiles;
export const selectMediaLoading = (state: RootState) => state.media.loading;
export const selectMediaError = (state: RootState) => state.media.error;
export const selectMediaFileCache = (state: RootState) => state.media.mediaFileCache;

export default mediaSlice.reducer;
